import type { DataProxy } from 'apollo-cache';
import { createStore, createHook, createActionsHook, createStateHook } from 'react-sweet-state';
import type { StoreActionApi } from 'react-sweet-state';
import isEqual from 'lodash/isEqual';

import { toDocumentNode } from '@confluence/apollo-relay-interop/entry-points/toDocumentNode.ts';
import type { ApolloInteropMutableHelper } from '@confluence/apollo-relay-interop/entry-points/ApolloInteropMutableHelper.ts';
import { buildConsistentActiveCommentsQueryVariables } from '@confluence/comments-panel-queries/entry-points/buildConsistentQueryVariablesHelper.ts';
import {
	type ActiveCommentsQueryType,
	type ActiveCommentsQueryVariables,
	type ActiveCommentsPanelNode,
	type ActiveCommentsPanelReply,
	type CommentInlineLocation,
} from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.ts';
import { ActiveCommentsQuery } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.graphql.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import type {
	TopLevelComment as BaseComment,
	CommentReply as BaseReply,
	ReactionsSummary,
	ResolvedProperties,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import { getLogger } from '@confluence/logger';

// eslint-disable-next-line import/no-cycle
import { type FragmentReaderInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
import { type FragmentWriterInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';

import { parseHasNestedComments } from './helper/commentsDataHelper.ts';

const logger = getLogger('comments-data');

export enum CommentType {
	INLINE = 'inline',
	GENERAL = 'general',
}

export enum CommentActionType {
	CREATE_COMMENT = 'created',
	DELETE_COMMENT = 'deleted',
	RESOLVE_COMMENT_THREAD = 'resolved',
	REOPEN_COMMENT_THREAD = 'reopened',
	REATTACH_COMMENT = 'reattach',
}

export interface AnnotationStatus {
	threadKey: string;
	isLoaded: boolean;
}

export interface ReplyData extends BaseReply {
	/**
	 * @deprecated Use commentThreadsRemovedByOtherUsers map from useCommentsDataState instead
	 * Check if a comment is removed using: commentThreadsRemovedByOtherUsers[commentId]
	 */
	wasRemovedByAnotherUser:
		| false
		| CommentActionType.DELETE_COMMENT
		| CommentActionType.RESOLVE_COMMENT_THREAD;
}

export interface CommentData extends BaseComment {
	isOpen: boolean; // unresolved comment
	type: CommentType;
	/**
	 * @deprecated Use commentThreadsRemovedByOtherUsers map from useCommentsDataState instead
	 * Check if a comment is removed using: commentThreadsRemovedByOtherUsers[commentId]
	 */
	wasRemovedByAnotherUser:
		| false
		| CommentActionType.DELETE_COMMENT
		| CommentActionType.RESOLVE_COMMENT_THREAD;
	replies: ReplyData[];
}

export type DataMap = Record<string, CommentData>;
export type CommentsDataMap = {
	inline: DataMap; // source of truth that maps annotation to a comment thread
	general: DataMap;
};

// Maps annotation position on page to threadKey, used to show the removed comment thread in the view
type RemovedThreadsMap = Record<number, string>;
export type RemovedThreadsCommentTypeMap = {
	inline: RemovedThreadsMap;
	general: RemovedThreadsMap;
};

export enum FooterCommentsLoadingStateType {
	NOT_LOADED = 'not_loaded',
	PRELOADING = 'preloading',
	PARTIALLY_LOADED = 'partially_loaded',
	FULLY_LOADED = 'fully_loaded',
}

export type RemovedCommentsMap = Record<
	string,
	CommentActionType.DELETE_COMMENT | CommentActionType.RESOLVE_COMMENT_THREAD
>;

export type CommentsDataState = {
	orderedActiveAnnotationIdList: AnnotationStatus[]; // ordered list of annotations on the page
	commentsDataMap: CommentsDataMap; // Deprecated when cc_comments_new_comments_data_manager is enabled - be careful updating this
	removedThreadsMap: RemovedThreadsCommentTypeMap;
	commentThreadsRemovedByOtherUsers: RemovedCommentsMap; // parent comment ID -> action type ('deleted' or 'resolved')
	hasNestedComments: boolean;
	cacheUpdateFailed: boolean;
	footerCommentsLoadingState: FooterCommentsLoadingStateType;
};

export const getInitialState = (): CommentsDataState => ({
	orderedActiveAnnotationIdList: [],
	commentsDataMap: {
		inline: {},
		general: {},
	},
	removedThreadsMap: {
		inline: {},
		general: {},
	},
	commentThreadsRemovedByOtherUsers: {},
	hasNestedComments: false,
	cacheUpdateFailed: false,
	footerCommentsLoadingState: FooterCommentsLoadingStateType.NOT_LOADED,
});

export const actions = {
	setCommentsDataMap:
		(newCommentsDataMap: CommentsDataMap) =>
		({ getState, setState }: StoreActionApi<CommentsDataState>) => {
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) return;
			const { orderedActiveAnnotationIdList } = getState();

			const parentCommentThreadKeys = new Set(Object.keys(newCommentsDataMap.inline));

			// Update the loaded status of annotations based on newCommentsDataMap
			const updatedAnnotationList = orderedActiveAnnotationIdList.map((annotation) => ({
				...annotation,
				isLoaded: parentCommentThreadKeys.has(annotation.threadKey),
			}));

			const hasNestedComments = parseHasNestedComments(newCommentsDataMap);

			setState({
				commentsDataMap: newCommentsDataMap,
				orderedActiveAnnotationIdList: updatedAnnotationList,
				hasNestedComments,
			});
		},
	setOrderedActiveAnnotationIdList:
		(newCommentsList: string[]) =>
		({ getState, setState }: StoreActionApi<CommentsDataState>) => {
			const { orderedActiveAnnotationIdList } = getState();
			if (!newCommentsList || newCommentsList.length === 0) {
				// Only update state if the current list is not already empty
				if (orderedActiveAnnotationIdList.length > 0) {
					setState({ orderedActiveAnnotationIdList: [] });
				}
				return;
			}

			// Create a map of existing annotationId to their isLoaded states for quick lookup
			const existingAnnotationsMap = new Map(
				orderedActiveAnnotationIdList.map(({ threadKey, isLoaded }) => [threadKey, isLoaded]),
			);
			// Use a Set to ensure unique annotation IDs
			const uniqueCommentsSet = new Set(newCommentsList);
			// Map the unique comments set to the new state, preserving isLoaded where possible
			const updatedAnnotationList = Array.from(uniqueCommentsSet).map((threadKey) => ({
				threadKey,
				isLoaded: existingAnnotationsMap.get(threadKey) ?? false, // Preserve existing isLoaded or default to false
			}));

			if (!isEqual(orderedActiveAnnotationIdList, updatedAnnotationList)) {
				setState({
					orderedActiveAnnotationIdList: updatedAnnotationList,
				});
			}
		},
	updateReactionsSummary:
		(
			emojiId: string,
			actionType: 'add' | 'delete',
			commentId: string,
			commentType: CommentType,
			readReactionsSummaryFragment: FragmentReaderInterop<any>,
			writeReactionsSummaryFragment: FragmentWriterInterop<any>,
			pageId?: string,
			threadKey?: string,
			cache?: DataProxy,
		) =>
		({ setState, getState }: StoreActionApi<CommentsDataState>) => {
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) return;
			const updateReactionsForComment = (
				commentData:
					| CommentData
					| ApolloInteropMutableHelper<ActiveCommentsPanelNode>
					| ReplyData
					| ApolloInteropMutableHelper<ActiveCommentsPanelReply>,
				emojiId: string,
				actionType: 'add' | 'delete',
			) => {
				const { reactionsSummary } = commentData || {};

				if (!reactionsSummary) {
					return;
				}

				const reactionsAri = reactionsSummary?.ari || '';
				const reactionsContainerAri = reactionsSummary?.containerAri || '';
				const reactionsNodes = reactionsSummary?.reactionsSummaryForEmoji || [];
				const updatedReactionsNodes = [...reactionsNodes];
				const updatedReactionsSummary = { ...reactionsSummary };
				if (actionType === 'add') {
					const reaction = updatedReactionsNodes.find((item) => item?.emojiId === emojiId);

					// If the reaction exists and the user has not reacted, add the user and increment the count
					if (reaction && !reaction.reacted) {
						reaction.count++;
						reaction.reacted = true;
					} else if (!reaction) {
						// If the reaction does not exist, create a new reaction
						updatedReactionsNodes.push({
							emojiId,
							count: 1,
							reacted: true,
							id: `${reactionsContainerAri}|${reactionsAri}|${emojiId}`,
						});
					}
				} else if (actionType === 'delete') {
					const reactionIdx = updatedReactionsNodes.findIndex((item) => item?.emojiId === emojiId);

					// If the reaction exists, update its attributes
					if (reactionIdx !== -1) {
						const reaction = updatedReactionsNodes[reactionIdx];

						// If the reaction exists and the user has reacted, remove the user and decrement the count
						if (reaction && reaction.reacted) {
							reaction.count--;
							reaction.reacted = false;
						}

						// If the reaction exists and has no reactions, remove the reaction
						if (reaction && reaction.count <= 0) {
							updatedReactionsNodes.splice(reactionIdx, 1);
						}
					}
				}

				updatedReactionsSummary.reactionsSummaryForEmoji = updatedReactionsNodes;
				updatedReactionsSummary.reactionsCount = updatedReactionsNodes.reduce(
					(total, reaction) => total + (reaction?.count || 0),
					0,
				);

				if (commentData) {
					commentData.reactionsSummary = updatedReactionsSummary as ReactionsSummary;
				}
			};

			try {
				if (expValEquals('remove_usages_of_cache_read_write', 'isEnabled', true)) {
					const reactionsSummaryId = `$Comment:${commentId}.reactionsSummary(${JSON.stringify({ childType: 'comment', contentType: 'page', pageId })})`;

					const reactionsSummary = readReactionsSummaryFragment({
						id: reactionsSummaryId,
					});
					if (reactionsSummary) {
						const commentReactionsSummary = { ...reactionsSummary };

						updateReactionsForComment(commentReactionsSummary, emojiId, actionType);
						writeReactionsSummaryFragment({
							fragmentId: reactionsSummaryId,
							data: commentReactionsSummary,
						});
					}
					const repliesReactionsSummaryId = `$Comment:${commentId}.replies.reactionsSummary(${JSON.stringify({ childType: 'comment', contentType: 'page', pageId })})`;

					const repliesReactionsSummary = readReactionsSummaryFragment({
						id: repliesReactionsSummaryId,
					});
					if (repliesReactionsSummary) {
						const updatedRepliesReactionsSummary = { reactionsSummary: repliesReactionsSummary };
						updateReactionsForComment(repliesReactionsSummary, emojiId, actionType);
						writeReactionsSummaryFragment({
							fragmentId: repliesReactionsSummaryId,
							data: updatedRepliesReactionsSummary,
						});
					}
				} else {
					const queryVariables = buildConsistentActiveCommentsQueryVariables({
						pageId: pageId!,
						type: commentType === CommentType.GENERAL ? 'FOOTER' : undefined, // If we have footer, always write to footer. Panel works either way
					});

					// Update the comments panel cache
					const cacheResult = cache?.readQuery<
						ActiveCommentsQueryType,
						ActiveCommentsQueryVariables
					>({
						query: toDocumentNode(ActiveCommentsQuery),
						variables: queryVariables,
					}) as ApolloInteropMutableHelper<ActiveCommentsQueryType>;

					if (cacheResult) {
						const commentNodes = cacheResult.comments?.nodes || [];
						const comment = commentNodes.find((node: any) =>
							commentType === CommentType.GENERAL
								? node?.id === threadKey
								: (node?.location as CommentInlineLocation)?.inlineMarkerRef === threadKey,
						);
						let reply: ReplyData | undefined;

						if (comment) {
							// If the comment IDs don't match, this is a reply, find it and use that
							if (comment.id !== commentId) {
								reply = (comment.replies as ReplyData[]).find((r) => r.id === commentId);

								// If we don't find the reply after that, just return
								if (!reply) {
									return;
								}
							}

							updateReactionsForComment(reply || comment, emojiId, actionType);

							cache?.writeQuery({
								query: toDocumentNode(ActiveCommentsQuery),
								variables: queryVariables,
								data: cacheResult,
							});
						}
					}
				}
			} catch (err) {
				logger.error`An error occurred when updating comments panel cache - ${err}`;
			}

			// Update the comment datasource
			const { commentsDataMap } = getState();
			const updatedMap = { ...commentsDataMap };

			if (!threadKey) {
				return;
			}

			const comment: CommentData | undefined = updatedMap[commentType][threadKey];
			let reply: ReplyData | undefined;

			// If the comment doesn't exist in the map, just return
			if (!comment) {
				return;
			}

			// If the comment IDs don't match, this is a reply, find it and use that
			if (comment.id !== commentId) {
				reply = comment.replies.find((r: ReplyData) => r.id === commentId);

				// If we don't find the reply after that, just return
				if (!reply) {
					return;
				}
			}

			updateReactionsForComment(reply || comment, emojiId, actionType);

			setState({
				commentsDataMap: updatedMap,
			});
		},
	addNewCommentThreads:
		(newCommentThreads: CommentsDataMap) =>
		({ setState, getState }: StoreActionApi<CommentsDataState>) => {
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) return;
			const { commentsDataMap, orderedActiveAnnotationIdList, hasNestedComments } = getState();

			const updatedCommentsDataMap = { ...commentsDataMap };

			// Add the comment threads for inline
			for (const threadKey in newCommentThreads.inline) {
				const newCommentThread = newCommentThreads.inline[threadKey];
				updatedCommentsDataMap.inline[threadKey] = newCommentThread;
			}

			// Add the comment threads for general
			for (const threadKey in newCommentThreads.general) {
				const newCommentThread = newCommentThreads.general[threadKey];
				updatedCommentsDataMap.general[threadKey] = newCommentThread;
			}

			// Update the loaded status of annotations
			const updatedAnnotationList = orderedActiveAnnotationIdList.map((annotation) => ({
				...annotation,
				isLoaded: Object.keys(newCommentThreads).includes(annotation.threadKey)
					? true
					: annotation.isLoaded,
			}));

			// No need to reparse if we've already found a case
			const newHasNestedComments =
				hasNestedComments || parseHasNestedComments(updatedCommentsDataMap);
			setState({
				commentsDataMap: updatedCommentsDataMap,
				orderedActiveAnnotationIdList: updatedAnnotationList,
				hasNestedComments: newHasNestedComments,
			});
		},
	addReplyToCommentThread:
		(threadKey: string, reply: ReplyData, commentType: CommentType, sortReplies: boolean = false) =>
		({ setState, getState }: StoreActionApi<CommentsDataState>) => {
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) return;
			const { commentsDataMap } = getState();

			const updatedCommentsDataMap = { ...commentsDataMap };

			const parentCommentThread = updatedCommentsDataMap[commentType][threadKey];
			if (parentCommentThread) {
				// Ensure replies array exists
				const existingReplies = parentCommentThread.replies || [];
				// due to the nature of how we are sending multiple pubsub events are being sent for comment creation, we have to check for a duplicate before adding a reply
				const replyExists = existingReplies.some((existingReply) => existingReply.id === reply.id);
				if (!replyExists) {
					const updatedReplies = [...existingReplies, reply];
					if (
						sortReplies &&
						expVal('confluence_fe_delete_comment_optimistic', 'isEnabled', false)
					) {
						// sort replies by createdAtNonLocalized in ascending order
						// each createdAtNonLocalized is a string in ISO format, e.g. "2026-01-02T03:45:67.890Z"
						updatedReplies.sort((a, b) =>
							a.createdAtNonLocalized.localeCompare(b.createdAtNonLocalized),
						);
					}

					const updatedParentCommentThread = {
						...parentCommentThread,
						replies: updatedReplies,
					};

					updatedCommentsDataMap[commentType][threadKey] = updatedParentCommentThread;

					setState({
						commentsDataMap: updatedCommentsDataMap,
					});
				}
			}
		},
	getCurrentCommentsPanelState:
		() =>
		({ getState }: StoreActionApi<CommentsDataState>) => {
			return getState();
		},
	setFooterCommentsLoadingState:
		(footerCommentsLoadingState: FooterCommentsLoadingStateType) =>
		({ setState }: StoreActionApi<CommentsDataState>) => {
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) return;
			setState({ footerCommentsLoadingState });
		},
	updateCommentBody:
		(
			commentId: string,
			threadKey: string,
			newBodyValue: string,
			commentType: CommentType,
			displayName: string | null = null, // Ensure displayName is not undefined for typechecking
		) =>
		({ setState, getState }: StoreActionApi<CommentsDataState>) => {
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) return;
			// Find the comment in the map
			const { commentsDataMap } = getState();

			// Make a copy of the map so if looks like a new object in shallow comparisons and also
			// avoids mutating the original map
			const updatedMap = { ...commentsDataMap };

			// The "parentComment" is either the comment itself (if a top-level comment) or the parent of
			// the reply thread
			const parentComment = updatedMap[commentType][threadKey];
			if (parentComment) {
				// If the modified comment is a top-level comment, update the body value
				if (parentComment.id === commentId) {
					// Make a copy of the comment and update the body value, then put it back into the map
					const updatedComment = {
						...parentComment,
						body: { value: newBodyValue },
						version: {
							...parentComment.version,
							by: {
								...parentComment.version?.by,
								displayName,
							},
							number: (parentComment.version?.number ?? 0) + 1,
							when: new Date().toISOString(),
						},
					};
					updatedMap[commentType][threadKey] = updatedComment;

					setState({ commentsDataMap: updatedMap });
				} else {
					// Else we didn't find the comment at the top-level, check the replies
					for (let replyIndex = 0; replyIndex < parentComment.replies.length; replyIndex++) {
						const reply = parentComment.replies[replyIndex];
						if (reply.id === commentId) {
							// Make a copy of the comment and update the reply's body value, then put it back into the map
							const updatedReply = {
								...reply,
								body: { value: newBodyValue },
								version: {
									...reply.version,
									by: {
										...reply.version?.by,
										displayName,
									},
									number: (reply.version?.number ?? 0) + 1,
									when: new Date().toISOString(),
								},
							};
							// (A reminder that ... does a shallow copy, so we need to copy the replies array as well)
							const updatedComment = {
								...parentComment,
								replies: [...parentComment.replies],
							};
							updatedComment.replies[replyIndex] = updatedReply;
							updatedMap[commentType][threadKey] = updatedComment;

							setState({ commentsDataMap: updatedMap });
							break;
						}
					}
				}
			}
		},
	// keep track of removed annotations as well as update removed comments with wasRemovedByAnotherUser
	updateRemovedCommentIdsMap:
		({
			threadKey,
			commentId,
			commentActionType,
			isReply,
			commentType,
			resolvedProperties,
		}: {
			threadKey: string;
			commentId: string;
			commentActionType:
				| CommentActionType.DELETE_COMMENT
				| CommentActionType.RESOLVE_COMMENT_THREAD;
			isReply: boolean;
			commentType: CommentType;
			resolvedProperties?: {
				inlineResolveProperties?: ResolvedProperties; // inline
				inlineText?: string; // inline
				resolved?: boolean; // footer
				resolvedFriendlyDate?: string | null; // footer
				resolvedTime?: number | null; // footer
				resolvedUser?: string | null; // footer
			};
		}) =>
		({ setState, getState }: StoreActionApi<CommentsDataState>) => {
			const { commentThreadsRemovedByOtherUsers } = getState();

			if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				const { commentsDataMap, orderedActiveAnnotationIdList, removedThreadsMap } = getState();
				const updatedCommentsDataMap = { ...commentsDataMap };
				const commentThread = updatedCommentsDataMap[commentType][threadKey];

				if (commentThread) {
					if (isReply) {
						const updatedCommentThreadReplies = commentThread.replies.map((r) => {
							if (r.id === commentId) {
								return {
									...r,
									wasRemovedByAnotherUser: commentActionType,
								};
							}
							return r;
						});

						updatedCommentsDataMap[commentType][threadKey] = {
							...commentThread,
							replies: updatedCommentThreadReplies,
						};
					} else {
						if (resolvedProperties) {
							if (
								(commentType === CommentType.INLINE ||
									!fg('confluence_gen_comments_resolve_pubsub')) &&
								resolvedProperties.inlineResolveProperties
							) {
								updatedCommentsDataMap[commentType][threadKey] = {
									...commentThread,
									location: {
										type: 'INLINE',
										inlineMarkerRef: threadKey,
										inlineResolveProperties: resolvedProperties.inlineResolveProperties,
										inlineText: resolvedProperties.inlineText || '',
									},
									isOpen: false,
									wasRemovedByAnotherUser: commentActionType,
								};
							} else {
								updatedCommentsDataMap[commentType][threadKey] = {
									...commentThread,
									location: {
										commentResolveProperties: {
											resolved: resolvedProperties.resolved || false,
											resolvedFriendlyDate: resolvedProperties.resolvedFriendlyDate || '',
											resolvedTime: resolvedProperties.resolvedTime || 0,
											resolvedUser: resolvedProperties.resolvedUser || '',
										},
										type: 'GENERAL',
									},
									isOpen: false,
									wasRemovedByAnotherUser: commentActionType,
								};
							}
						} else {
							updatedCommentsDataMap[commentType][threadKey] = {
								...commentThread,
								isOpen: false,
								wasRemovedByAnotherUser: commentActionType,
							};
						}
					}

					const updatedRemovedThreadsMap = { ...removedThreadsMap };
					let threadKeyId = -1;
					if (commentType === CommentType.INLINE) {
						// since orderedActiveAnnotationIdList will not include previously removed annotations, we need to add in annotations in removedThreadsMap
						// so that we can correctly find the index of the removed annotation
						const inlineThreadKeyList = [...orderedActiveAnnotationIdList.map((a) => a.threadKey)];

						Object.entries(updatedRemovedThreadsMap.inline).forEach(([index, key]) => {
							const position = parseInt(index, 10); // Ensure the index is treated as a number
							inlineThreadKeyList.splice(position, 0, key);
						});

						threadKeyId = inlineThreadKeyList.findIndex((key) => key === threadKey);
					} else {
						// General comments are ordered correctly in the map by timestamp
						const orderedGeneralThreadKeyList = Object.keys(commentsDataMap.general);

						threadKeyId = orderedGeneralThreadKeyList.findIndex((key) => key === threadKey);
					}

					if (threadKeyId !== -1) {
						updatedRemovedThreadsMap[commentType][threadKeyId] = threadKey;
					}

					setState({
						commentsDataMap: updatedCommentsDataMap,
						removedThreadsMap: updatedRemovedThreadsMap,
					});
				}
			}

			// Update commentThreadsRemovedByOtherUsers map
			const updatedRemovedComments = { ...commentThreadsRemovedByOtherUsers };
			updatedRemovedComments[commentId] = commentActionType;

			// Internally, this will be batched with the other state updates when cc_comments_new_comments_data_manager is disabled
			setState({
				commentThreadsRemovedByOtherUsers: updatedRemovedComments,
			});
		},
	handleRemovingComments:
		({
			threadKey,
			commentId,
			action,
			commentType,
		}: {
			threadKey: string;
			commentId?: string;
			action: CommentActionType;
			commentType: CommentType;
		}) =>
		({ setState, getState }: StoreActionApi<CommentsDataState>) => {
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) return;
			const { commentsDataMap } = getState();

			const updatedCommentsDataMap = { ...commentsDataMap };
			const parentCommentThread = updatedCommentsDataMap[commentType][threadKey];

			if (parentCommentThread) {
				if (
					action === CommentActionType.RESOLVE_COMMENT_THREAD &&
					parentCommentThread.id === commentId
				) {
					// If the action is to resolve the thread and the commentId matches the parent, remove the thread
					delete updatedCommentsDataMap[commentType][threadKey];
					setState({
						commentsDataMap: updatedCommentsDataMap,
					});
				} else if (action === CommentActionType.DELETE_COMMENT) {
					// If the action is to delete a reply, filter out the reply with the specified commentId
					const updatedReplies = parentCommentThread.replies.filter(
						(reply) => reply.id !== commentId,
					);

					updatedCommentsDataMap[commentType][threadKey] = {
						...parentCommentThread,
						replies: updatedReplies,
					};
					// Update the parent comment thread with the filtered replies
					setState({
						commentsDataMap: updatedCommentsDataMap,
					});
				}
			}
		},
	resolveParentComment:
		({ threadKey, commentType }: { threadKey: string; commentType: CommentType }) =>
		({ setState, getState }: StoreActionApi<CommentsDataState>) => {
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) return;
			const { commentsDataMap } = getState();

			const updatedCommentsDataMap = { ...commentsDataMap };
			const parentCommentThread = updatedCommentsDataMap[commentType][threadKey];

			if (parentCommentThread) {
				parentCommentThread.isOpen = false;

				setState({
					commentsDataMap: updatedCommentsDataMap,
				});
			}
		},
	reopenParentComment:
		({
			threadKey,
			commentType,
			parentCommentId,
		}: {
			threadKey: string;
			commentType: CommentType;
			parentCommentId?: string;
		}) =>
		({ setState, getState }: StoreActionApi<CommentsDataState>) => {
			// With new data manager enabled we ONLY update commentThreadsRemovedByOtherUsers.
			// Once the experiment is out, we should probably rename this function
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				if (!parentCommentId) {
					return;
				}
				const { commentThreadsRemovedByOtherUsers: currentRemovedComments } = getState();
				const updatedRemovedComments = { ...currentRemovedComments };
				delete updatedRemovedComments[parentCommentId];
				setState({
					commentThreadsRemovedByOtherUsers: updatedRemovedComments,
				});
				return;
			}

			const { commentsDataMap, commentThreadsRemovedByOtherUsers } = getState();

			const updatedCommentsDataMap = { ...commentsDataMap };
			const parentCommentThread = updatedCommentsDataMap[commentType][threadKey];

			if (parentCommentThread) {
				parentCommentThread.isOpen = true;

				// Remove from commentThreadsRemovedByOtherUsers when reopened
				const updatedRemovedComments = { ...commentThreadsRemovedByOtherUsers };
				delete updatedRemovedComments[parentCommentThread.id];

				setState({
					commentsDataMap: updatedCommentsDataMap,
					commentThreadsRemovedByOtherUsers: updatedRemovedComments,
				});
			}
		},
	deleteParentComment:
		({ threadKey, commentType }: { threadKey: string; commentType: CommentType }) =>
		({ setState, getState }: StoreActionApi<CommentsDataState>) => {
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) return;
			const { commentsDataMap } = getState();

			const updatedCommentsDataMap = { ...commentsDataMap };
			const parentCommentThread = updatedCommentsDataMap[commentType][threadKey];

			if (parentCommentThread) {
				delete updatedCommentsDataMap[commentType][threadKey];

				setState({
					commentsDataMap: updatedCommentsDataMap,
				});
			}
		},
	clearRemovedComments:
		() =>
		({ getState, setState }: StoreActionApi<CommentsDataState>) => {
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				setState({
					commentThreadsRemovedByOtherUsers: {},
				});
				return;
			}

			const { commentsDataMap } = getState();

			const commentsDataMapCopy = { ...commentsDataMap };

			// Filter out deleted / resolved comments for the legacy comments data map
			const updatedInlineCommentsDataMap = Object.fromEntries(
				Object.entries(commentsDataMapCopy.inline)
					.filter(([_, commentData]) => !commentData.wasRemovedByAnotherUser)
					.map(([key, commentData]) => [
						key,
						{
							...commentData,
							replies: (commentData.replies || []).filter(
								(reply) => !reply.wasRemovedByAnotherUser,
							),
						},
					]),
			);

			const updatedGeneralCommentsDataMap = Object.fromEntries(
				Object.entries(commentsDataMapCopy.general)
					.filter(([_, commentData]) => !commentData.wasRemovedByAnotherUser)
					.map(([key, commentData]) => [
						key,
						{
							...commentData,
							replies: (commentData.replies || []).filter(
								(reply) => !reply.wasRemovedByAnotherUser,
							),
						},
					]),
			);

			setState({
				removedThreadsMap: { inline: {}, general: {} },
				commentThreadsRemovedByOtherUsers: {},
				commentsDataMap: {
					inline: updatedInlineCommentsDataMap,
					general: updatedGeneralCommentsDataMap,
				},
			});
		},
	clearCommentsData:
		() =>
		({ setState }: StoreActionApi<CommentsDataState>) => {
			setState(getInitialState());
		},
	setCacheUpdateFailed:
		(failed: boolean) =>
		({ setState }: StoreActionApi<CommentsDataState>) => {
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) return;
			setState({ cacheUpdateFailed: failed });
		},
};

export const CommentsDataStore = createStore({
	initialState: getInitialState(),
	actions,
	name: 'CommentsDataStore',
});

export const useCommentsData = createHook(CommentsDataStore);
export const useCommentsDataActions = createActionsHook(CommentsDataStore);
export const useCommentsDataState = createStateHook(CommentsDataStore);
